import { Type, Circle, Square } from "lucide-react";
import { useBuilderState } from '../../hooks/useBuilderState';
import { useBuilderNode } from '../../hooks/useBuilderNode';
import { createTextNode, createShapeNode } from '../../utils/nodeUtils';
import type { ShapeNodeData } from '../../types/node.types';

interface ToolItem {
  id: string;
  icon: (props: { size: number; strokeWidth: number }) => React.ReactNode;
  label: string;
  action: () => void;
  dragData?: string;
}

interface ToolSection {
  label: string;
  tools: ToolItem[];
}

export const ToolsBar = () => {
  const { surfaces, currentSurface, addNode, productConfig } = useBuilderState();
  const { selectNode } = useBuilderNode();

  const currentLayers = surfaces[currentSurface] || [];
  const maxZIndex = currentLayers.length > 0 ? Math.max(...currentLayers.map((n) => n.zIndex)) : 0;

  const handleAddText = () => {
    const dims = productConfig.dimensions[currentSurface];
    const textW = 200, textH = 50;
    const node = createTextNode(currentSurface, productConfig.type, { x: ((dims?.width ?? 440) - textW) / 2, y: ((dims?.height ?? 600) - textH) / 2 });
    node.zIndex = maxZIndex + 1;
    addNode(node);
    selectNode(node.id);
  };

  const handleAddShape = (shapeType: ShapeNodeData["shapeType"]) => {
    const size = shapeType === "circle" ? { width: 100, height: 100 } : { width: 100, height: 100 };
    const dims = productConfig.dimensions[currentSurface];
    const node = createShapeNode(shapeType, currentSurface, productConfig.type, { x: ((dims?.width ?? 440) - size.width) / 2, y: ((dims?.height ?? 600) - size.height) / 2 }, size);
    node.zIndex = maxZIndex + 1;
    addNode(node);
    selectNode(node.id);
  };

  const sections: ToolSection[] = [
    {
      label: "Text",
      tools: [
        { id: "text", icon: Type, label: "Text", action: handleAddText, dragData: JSON.stringify({ type: "text" }) },
      ],
    },
    {
      label: "Shapes",
      tools: [
        { id: "circle", icon: Circle, label: "Circle", action: () => handleAddShape("circle"), dragData: JSON.stringify({ type: "shape", shapeType: "circle" }) },
        { id: "rectangle", icon: Square, label: "Rectangle", action: () => handleAddShape("rectangle"), dragData: JSON.stringify({ type: "shape", shapeType: "rectangle" }) },
      ],
    },
  ];

  return (
    <div className="flex flex-col items-center gap-0.5 bg-white rounded-lg border border-gray-200 shadow-lg p-1">
      {sections.map((section, sectionIdx) => (
        <div key={section.label} className="flex flex-col items-center gap-0.5">
          {sectionIdx > 0 && <div className="w-5 h-px bg-gray-200 my-0.5" />}
          {section.tools.map(({ id, icon: Icon, label, action, dragData }) => (
            <button
              key={id}
              onClick={action}
              title={label}
              draggable={!!dragData}
              onDragStart={(e) => {
                if (dragData) {
                  e.dataTransfer.setData("text/plain", dragData);
                  e.dataTransfer.effectAllowed = "copy";
                }
              }}
              className="w-8 h-8 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors cursor-pointer active:bg-gray-200"
            >
              <Icon size={16} strokeWidth={1.6} />
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};
